import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

export type EditVitalExecutionMode =
  | { mode: 'edit'; executionId: string; vitalType: string | null; initialValue: number | null; initialSecondary: number | null; initialNotes: string | null; taskName: string }
  | { mode: 'create' };

interface VitalTaskOption {
  id: string;
  name: string;
  vital_type: string;
  unit: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  state: EditVitalExecutionMode | null;
  onSaved?: () => void;
}

export function EditVitalExecutionDialog({ open, onOpenChange, patientId, state, onSaved }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [value, setValue] = useState('');
  const [secondary, setSecondary] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Create mode
  const [tasks, setTasks] = useState<VitalTaskOption[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');

  useEffect(() => {
    if (!open || !state) return;
    if (state.mode === 'edit') {
      setValue(state.initialValue != null ? String(state.initialValue) : '');
      setSecondary(state.initialSecondary != null ? String(state.initialSecondary) : '');
      setNotes(state.initialNotes ?? '');
      setReason('');
    } else {
      setValue('');
      setSecondary('');
      setNotes('');
      setReason('');
      setSelectedTaskId('');
      (async () => {
        const { data } = await supabase
          .from('care_plan_tasks')
          .select('id, name, vital_type, unit')
          .eq('patient_id', patientId)
          .eq('task_type', 'vital_sign')
          .eq('is_active', true)
          .order('name');
        setTasks(((data ?? []) as any[]).filter(t => t.vital_type) as VitalTaskOption[]);
      })();
    }
  }, [open, state, patientId]);

  const currentVitalType =
    state?.mode === 'edit'
      ? state.vitalType
      : tasks.find(t => t.id === selectedTaskId)?.vital_type ?? null;
  const isBP = currentVitalType === 'blood_pressure';

  const handleSubmit = async () => {
    if (!state || !user) return;

    if (!value.trim()) {
      toast.error(t('executeTask.value', { defaultValue: 'Valor' }) + ' obrigatório');
      return;
    }
    if (isBP && !secondary.trim()) {
      toast.error(t('executeTask.bothValuesRequired', { defaultValue: 'Preencha os dois valores.' }));
      return;
    }

    setSubmitting(true);
    try {
      if (state.mode === 'edit') {
        if (!reason.trim()) {
          toast.error('Informe o motivo da correção');
          setSubmitting(false);
          return;
        }
        const { error } = await supabase
          .from('task_executions')
          .update({
            vital_value: parseFloat(value),
            vital_value_secondary: secondary ? parseFloat(secondary) : null,
            notes: notes.trim() || null,
            edit_reason: reason.trim(),
          })
          .eq('id', state.executionId);
        if (error) throw error;
        toast.success('Medição atualizada');
      } else {
        if (!selectedTaskId) {
          toast.error('Selecione um sinal vital');
          setSubmitting(false);
          return;
        }
        const { error } = await supabase.from('task_executions').insert({
          task_id: selectedTaskId,
          patient_id: patientId,
          executed_by: user.id,
          vital_value: parseFloat(value),
          vital_value_secondary: secondary ? parseFloat(secondary) : null,
          notes: notes.trim() || null,
          is_sporadic: false,
        });
        if (error) throw error;
        toast.success('Medição registrada');
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {state?.mode === 'edit' ? `Editar: ${state.taskName}` : 'Registrar sinal vital'}
          </DialogTitle>
          {state?.mode === 'edit' && (
            <DialogDescription>
              A correção fica registrada para auditoria.
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {state?.mode === 'create' && (
            <div className="space-y-2">
              <Label>Sinal vital</Label>
              <Select value={selectedTaskId} onValueChange={setSelectedTaskId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {tasks.map(opt => (
                    <SelectItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tasks.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Nenhuma tarefa de sinal vital ativa para este paciente.
                </p>
              )}
            </div>
          )}

          {isBP ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Sistólica</Label>
                <Input type="number" value={value} onChange={e => setValue(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Diastólica</Label>
                <Input
                  type="number"
                  value={secondary}
                  onChange={e => setSecondary(e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Valor</Label>
              <Input
                type="number"
                step="0.1"
                value={value}
                onChange={e => setValue(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Observação</Label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Opcional"
            />
          </div>

          {state?.mode === 'edit' && (
            <div className="space-y-2">
              <Label>
                Motivo da correção <span className="text-destructive">*</span>
              </Label>
              <Textarea
                value={reason}
                onChange={e => setReason(e.target.value.slice(0, 200))}
                rows={2}
                placeholder="Ex.: digitação incorreta"
                maxLength={200}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
